<template>
    <div class="search-input-wrapper" v-click-outside="blur">
        <IconTextbox
            :class="{ focused }"
            @enter="viewAll"
            type="text"
            @focus="focus"
            v-model="internalSearchText"
            :placeholder="placeholder"
            class="mt-search-input"
            :disabled="disabled"
            :label="label"
            :errorMessage="errorMessage"
        />
        <div class="search-input-absolute" v-if="showSearchResults">
            <div class="search-dropdown">
                <div v-if="showPlaceholder">
                    <slot name="placeholder" />
                </div>
                <div v-else class="loading-container">
                    <CompanySearchResultsPanel
                        v-if="companiesVisible"
                        :companies="companies"
                        :maxItems="groupResultsLimit"
                        :showCount="hasSearchText"
                        :showViewAll="hasSearchText && showViewAll"
                        :title="companiesTitle"
                        @navigating="navigating"
                        @viewAll="viewAll('Companies')"
                    />

                    <ExecutiveSearchResultsPanel
                        v-if="executivesVisible"
                        :executives="executives"
                        :maxItems="groupResultsLimit"
                        :showCount="hasSearchText"
                        :showViewAll="hasSearchText && showViewAll"
                        :title="executivesTitle"
                        @navigating="navigating"
                        @viewAll="viewAll('Executives')"
                    />

                    <InterviewSearchResultPanel
                        v-if="interviewsVisible"
                        :interviews="interviews"
                        :maxItems="groupResultsLimit"
                        :showCount="hasSearchText"
                        :showViewAll="hasSearchText && showViewAll"
                        :title="interviewsTitle"
                        :query="searchText"
                        @navigating="navigating"
                        @viewAll="viewAll('Interviews')"
                    />
                    <loading :active="loading" :is-full-page="false" />
                </div>
            </div>
            <slot
                name="static-option-append"
                :searchText="searchText"
                :interviews="interviews"
                :executives="executives"
                :companies="companies"
            >
                <div class="static-option">
                    <div
                        @click.prevent="viewAll('All')"
                        class="more-results-link primary-link"
                        v-if="showMoreResultsLink"
                    >
                        Show All Results
                    </div>
                </div>
            </slot>
        </div>
    </div>
</template>
<script>
import RecommendationService from '@/services/recommendationService'
import SearchService from '@/services/searchService'
import debounce from 'debounce-promise'
import entityTypes from '@/utils/entityTypes'

import IconTextbox from '../shared/icon-textbox.vue'
import CompanySearchResultsPanel from './CompanySearchResultsPanel.vue'
import ExecutiveSearchResultsPanel from './ExecutiveSearchResultsPanel.vue'
import InterviewSearchResultPanel from './InterviewSearchResultPanel.vue'

function defaultResult() {
    return { results: [], meta: { resultCount: 0 } }
}

export default {
    components: {
        IconTextbox,
        CompanySearchResultsPanel,
        ExecutiveSearchResultsPanel,
        InterviewSearchResultPanel,
    },
    data() {
        return {
            focused: false,
            internalSearchText: '',
        }
    },
    props: {
        disabled: {
            type: Boolean,
            default: false,
        },
        errorMessage: {
            type: [String, Array],
            required: false,
        },
        label: {
            type: String,
            default: null,
        },
        limit: {
            type: Number,
            default: 6,
        },
        searchText: {
            type: String,
            default: '',
        },
        searchTypes: {
            type: Array,
            default: () => null,
        },
        showViewAll: {
            type: Boolean,
            default: true,
        },
    },
    watch: {
        searchText: {
            handler(to) {
                this.internalSearchText = to
            },
            immediate: true,
        },
        internalSearchText(to) {
            this.$emit('update:searchText', this.internalSearchText)
        },
    },
    asyncComputed: {
        searchResults: {
            get() {
                if (this.hasSearchText) {
                    return this.search(this.internalSearchText, this.searchTypes, this.groupResultsLimit)
                } else {
                    return RecommendationService.getRecentlyViewed()
                }
            },
            default() {
                return null
            },
        },
    },
    computed: {
        companies() {
            return this.searchResults?.companies ?? defaultResult()
        },
        companiesTitle() {
            return this.hasSearchText ? 'Companies' : 'Recently Viewed Companies'
        },
        companiesVisible() {
            return this.searchTypes?.includes(entityTypes.company) ?? true
        },
        executives() {
            return this.searchResults?.executives ?? defaultResult()
        },
        executivesTitle() {
            return this.hasSearchText ? 'Executives' : 'Recently Viewed Executives'
        },
        executivesVisible() {
            return this.searchTypes?.includes(entityTypes.executive) ?? true
        },
        groupResultsLimit() {
            return this.searchTypes?.length === 1 ? this.limit : this.limit / 2
        },
        hasMoreCompanies() {
            return this.companiesVisible && this.companies.meta.resultCount > this.groupResultsLimit
        },
        hasMoreExecutives() {
            return this.executivesVisible && this.executives.meta.resultCount > this.groupResultsLimit
        },
        hasMoreInterviews() {
            return this.interviewsVisible && this.interviews.meta.resultCount > this.groupResultsLimit
        },
        hasSearchText() {
            return this.internalSearchText?.trim().length > 0
        },
        interviews() {
            return this.searchResults?.interviews ?? defaultResult()
        },
        interviewsTitle() {
            return 'Interviews'
        },
        interviewsVisible() {
            return (this.searchTypes?.includes(entityTypes.interview) ?? true) && this.hasSearchText
        },
        loading() {
            return this.$asyncComputed.searchResults.updating
        },
        placeholder() {
            const searchTypes = this.searchTypes ?? [entityTypes.executive, entityTypes.company, entityTypes.interview]

            const descriptors = []

            if (searchTypes.includes(entityTypes.executive)) {
                descriptors.push('Executives')
            }

            if (searchTypes.includes(entityTypes.company)) {
                descriptors.push('Companies')
            }

            if (searchTypes.includes(entityTypes.interview)) {
                descriptors.push('Interviews')
            }

            let placeholder = `Search ${descriptors[0]}`

            if (descriptors.length === 1) {
                return placeholder
            }

            for (let index = 1; index < descriptors.length - 1; index++) {
                placeholder += `, ${descriptors[index]}`
            }

            // oxford comma
            if (descriptors.length > 2) {
                placeholder += ','
            }

            return placeholder + ` and ${descriptors[descriptors.length - 1]}`
        },
        showPlaceholder() {
            return !!this.$slots?.placeholder && this.focused && !this.hasSearchText
        },
        showSearchResults() {
            return this.focused && (this.companiesVisible || this.executivesVisible || this.inteviewsVisible)
        },
        showMoreResultsLink() {
            return this.hasMoreCompanies || this.hasMoreExecutives || this.hasMoreInterviews
        },
    },
    methods: {
        blur() {
            this.focused = false
        },
        clear() {
            this.blur()
            this.internalSearchText = ''
        },
        focus() {
            this.focused = true
        },
        search: debounce(async function (searchText, searchTypes, limit) {
            return await SearchService.search(searchText, searchTypes, limit)
        }, 100),
        navigating(event, entity) {
            this.$emit('navigating', event, entity)
            this.blur()
        },
        showCustomWorkModal(item) {
            this.$store.getters.customWork.show({ executives: [item.entity] })
            this.blur()
        },
        viewAll(tab) {
            this.$emit('viewAll', this.internalSearchText, tab)
            this.blur()
        },
    },
}
</script>
<style>
.search-input-wrapper {
    position: relative;
    width: 100%;
    display: inline-block;

    i.search {
        position: absolute;
        top: 0;
        width: 40px;
        line-height: 40px;
        cursor: pointer;
        color: var(--color-primary-600);
        text-align: center;
    }

    .search-input-absolute {
        position: absolute;
        display: flex;
        flex-direction: column;
        right: 0;
        min-width: 768px;
        max-width: 100vh;
        background-color: white;
        border: 1px solid var(--color-primary-200);
        box-shadow: var(--boxshadow-default);
        z-index: var(--z-index-dropdown-menu);
        max-height: 85vh;
        top: 100%;
        width: 100%;

        .mt-search-input {
            position: relative;

            input,
            input:focus {
                border: var(--input-border-style);
            }
        }

        .search-dropdown {
            align-items: stretch;
            overflow: auto;
        }
    }

    .more-results-link {
        cursor: pointer;
        font-size: 14px;
        padding: 16px;
        text-align: center;
    }

    .empty-results {
        padding: 8px;

        h4 {
            text-align: center;
        }
    }

    .search-result-panel {
        padding: 0;

        .search-result {
            padding: 16px 16px;
        }

        .search-result-panel-title,
        .view-all {
            line-height: 48px;
            background-color: var(--color-primary-050);
            padding: 0 24px;
            margin: 0;
        }

        .search-result-panel-title {
            font-size: 13px;
            text-transform: uppercase;
            color: var(--color-primary-600);
        }
    }
}
</style>
